/**
 * Pure helpers for schedule toolbar filters: option lists from visible blocks and
 * block-level match tests for work-scope and worker filters.
 */

import { getWorkerDisplayLabel } from "../../../../utils/business/worker";
import { itemMatchesSearchQuery } from "../../../../utils/common/searchFilter";
import {
  collectUniqueFilterOptions,
  filterOptionIdsMatchingSearch,
} from "../../shared/filters/planningFilterModel";
import { getScheduleAspectForBlockOnDate } from "./scheduleBlockSelectors";
import { getScheduleBlockRowId } from "./scheduleRowModel";

/**
 * @typedef {import("../../shared/filters/planningFilterModel").PlanningFilterOption} ScheduleFilterOption
 */

/**
 * Unique work scopes on blocks, sorted by label.
 *
 * @param {Array<Object>} blocks
 * @returns {ScheduleFilterOption[]}
 */
export function collectWorkScopeFilterOptions(blocks) {
  return collectUniqueFilterOptions(
    (blocks ?? []).map((block) =>
      block?.scope?.id == null
        ? null
        : {
            value: block.scope.id,
            label: block.scope?.name?.trim() || String(block.scope.id),
          }
    )
  );
}

/**
 * Map visible projects to filter options, sorted by project-number label.
 *
 * @param {Array<Object>} projects
 * @returns {ScheduleFilterOption[]}
 */
export function collectProjectFilterOptions(projects) {
  return collectUniqueFilterOptions(
    (projects ?? []).map((project) => ({
      value: project.id,
      label: `${project.projectNumber} — ${project.name}`,
    }))
  );
}

/**
 * Unique workers on blocks and in-range per-day aspects (includes override crews).
 *
 * @param {Array<Object>} blocks
 * @param {Array<Object>} aspects
 * @param {Array<Date>} visibleDays
 * @returns {ScheduleFilterOption[]}
 */
export function collectWorkerFilterOptions(blocks, aspects, visibleDays) {
  const entries = [];
  for (const block of blocks ?? []) {
    for (const day of visibleDays ?? []) {
      const aspect = getScheduleAspectForBlockOnDate(aspects, block.id, day);
      const assignment = aspect
        ? aspect.worker_assignment ?? []
        : block.worker_assignment ?? [];
      for (const worker of assignment) {
        if (worker?.id == null) {
          continue;
        }
        entries.push({
          value: worker.id,
          label: getWorkerDisplayLabel(worker),
        });
      }
    }
  }
  return collectUniqueFilterOptions(entries);
}

/**
 * @param {Object} block
 * @param {Array<string>} scopeIds
 * @returns {boolean}
 */
export function blockMatchesWorkScopeFilter(block, scopeIds) {
  if (!scopeIds?.length) {
    return true;
  }
  return scopeIds.includes(String(block?.scope?.id));
}

/**
 * @param {Object} block
 * @param {Array<Object>} aspects
 * @param {Array<Date>} visibleDays
 * @param {Array<string>} workerIds
 * @returns {boolean}
 */
export function blockMatchesWorkerFilter(block, aspects, visibleDays, workerIds) {
  if (!workerIds?.length) {
    return true;
  }

  for (const day of visibleDays ?? []) {
    if (countSelectedWorkersOnScheduleBlockDay(block, aspects, day, workerIds) > 0) {
      return true;
    }
  }

  return false;
}

/**
 * Count how many of the selected workers are assigned on one block/day, using the
 * per-day aspect crew when present and falling back to the block-level default crew.
 *
 * @param {Object} block
 * @param {Array<Object>} aspects
 * @param {Date|null|undefined} day
 * @param {Array<string>} workerIds
 * @returns {number}
 */
export function countSelectedWorkersOnScheduleBlockDay(block, aspects, day, workerIds) {
  if (!workerIds?.length || !block || !day) {
    return 0;
  }
  const targetIds = new Set(
    workerIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
  );
  if (!targetIds.size) {
    return 0;
  }

  const aspect = getScheduleAspectForBlockOnDate(aspects, block.id, day);
  const assignment = aspect
    ? aspect.worker_assignment ?? []
    : block.worker_assignment ?? [];
  return assignment.reduce(
    (count, worker) => (targetIds.has(Number(worker?.id)) ? count + 1 : count),
    0
  );
}

/**
 * @param {Array<Object>} blocks
 * @param {Array<Object>} aspects
 * @param {Array<Date>} visibleDays
 * @param {Array<string>} workScopeIds
 * @param {Array<string>} workerIds
 * @returns {Array<Object>}
 */
export function filterScheduleBlocksForToolbar(
  blocks,
  aspects,
  visibleDays,
  workScopeIds,
  workerIds
) {
  let list = blocks ?? [];
  if (workScopeIds?.length) {
    list = list.filter((block) => blockMatchesWorkScopeFilter(block, workScopeIds));
  }
  if (workerIds?.length) {
    list = list.filter((block) =>
      blockMatchesWorkerFilter(block, aspects, visibleDays, workerIds)
    );
  }
  return list;
}

/**
 * Search-match a schedule block by the scope name shown on the bar.
 *
 * @param {Object} block
 * @param {string} query Raw search text.
 * @returns {boolean}
 */
export function scheduleBlockMatchesScopeSearch(block, query) {
  return itemMatchesSearchQuery(block, query, ["scope.name"]);
}

/**
 * Whether a block matches TopBar search via scope name or worker filter labels
 * (same labels/assignment rules as selecting those workers in the worker filter).
 *
 * @param {Object} block
 * @param {string} query Raw search text.
 * @param {Array<Object>} aspects WorkScopeAspect rows for per-day crews.
 * @param {Array<Date>} visibleDays Visible timeline days.
 * @param {Array<string>} [matchingWorkerIds] Precomputed worker ids whose labels match query.
 * @returns {boolean}
 */
export function scheduleBlockMatchesSearch(
  block,
  query,
  aspects,
  visibleDays,
  matchingWorkerIds = null
) {
  if (scheduleBlockMatchesScopeSearch(block, query)) {
    return true;
  }
  const workerIds =
    matchingWorkerIds ??
    filterOptionIdsMatchingSearch(
      collectWorkerFilterOptions([block], aspects, visibleDays),
      query
    );
  return (
    workerIds.length > 0 &&
    blockMatchesWorkerFilter(block, aspects, visibleDays, workerIds)
  );
}

/**
 * Keep scope blocks that match search by scope name or assigned workers
 * (worker labels from {@link collectWorkerFilterOptions}).
 *
 * @param {Array<Object>} blocks
 * @param {string} query Raw search text.
 * @param {Array<Object>} aspects WorkScopeAspect rows for per-day crews.
 * @param {Array<Date>} visibleDays Visible timeline days.
 * @returns {Array<Object>}
 */
export function filterScheduleBlocksForSearch(
  blocks,
  query,
  aspects,
  visibleDays
) {
  if (!query?.trim()) {
    return blocks ?? [];
  }
  const matchingWorkerIds = filterOptionIdsMatchingSearch(
    collectWorkerFilterOptions(blocks, aspects, visibleDays),
    query
  );
  return (blocks ?? []).filter((block) =>
    scheduleBlockMatchesSearch(
      block,
      query,
      aspects,
      visibleDays,
      matchingWorkerIds
    )
  );
}

/**
 * Filter the already-visible flat Schedule rows by project/group label or by a
 * matching visible scope block owned by the row (scope name or worker labels).
 *
 * @param {Object} params
 * @param {Array<Object>} params.rows Flat visible Schedule rows.
 * @param {Array<Object>} params.projects Visible projects for project-name matches.
 * @param {Array<Object>} params.blocks Visible scope blocks after toolbar filters.
 * @param {Array<Object>} params.aspects WorkScopeAspect rows for per-day crews.
 * @param {Array<Date>} params.visibleDays Visible timeline days.
 * @param {string} params.query Raw search text.
 * @returns {Array<Object>}
 */
export function filterScheduleRowsForSearch({
  rows,
  projects,
  blocks,
  aspects,
  visibleDays,
  query,
}) {
  if (!query?.trim()) {
    return rows ?? [];
  }

  const projectById = new Map(
    (projects ?? []).map((project) => [project.id, project])
  );
  const matchingBlocks = filterScheduleBlocksForSearch(
    blocks,
    query,
    aspects,
    visibleDays
  );
  const rowIdsWithMatchingScopeBlocks = new Set(
    matchingBlocks.map((block) => getScheduleBlockRowId(block))
  );

  return (rows ?? []).filter((row) => {
    const project = projectById.get(row.projectId);
    return (
      itemMatchesSearchQuery(row, query, ["label"]) ||
      itemMatchesSearchQuery(project, query, ["projectNumber", "name"]) ||
      rowIdsWithMatchingScopeBlocks.has(row.id)
    );
  });
}
